import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ISuperTableCalculation, ISuperTableCalculationFunction } from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IXAxisConfigColumn, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { SuperTableColumnBelongingYear } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/super_table_column_belonging_year.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash-es';
import { EMPTY_ARRAY_LENGTH } from '../../../../../../../aumico-lib-core-fe/src/lib';
import { ISuperTableAddCalculationResult } from '../../../../interfaces/super-table-add-calculation-result.interface';
import { ISuperTableItemSelection } from '../../../../interfaces/super-table-item-selection.interface';
import { SuperTableColumnWidth } from '@modeso/aumico-lib-notes-fe/enums/SuperTableColumnWidth.ebum';

@UntilDestroy()
@Component({
    selector: 'super-table-summation-column',
    templateUrl: './super-table-summation-column.component.html',
    styleUrls: ['./super-table-summation-column.component.scss'],
})
export class SuperTableSummationColumnComponent implements OnInit, OnChanges {
    @Input() columnSelectionOptions: IXAxisConfigColumn[] = [];
    @Input() rowSelectionOptions: ISuperTableItemSelection[] = [];
    @Input() existingCalculation: ISuperTableCalculation | undefined;
    @Input() language: string | undefined;
    existingSelectedColumns: INameId[] = [];
    selectedItems: INameId[] = [];
    columnWidthSelections: INameId[] = [];

    titleControl = new FormControl('');
    belongingYearControl = new FormControl(SuperTableColumnBelongingYear.NO_YEAR);
    columnWidthControl = new FormControl(SuperTableColumnWidth.STANDARD);
    belongingYearSelections: INameId[] = [];
    nameIdColSelections: INameId[] = [];

    @Output() calculationOutput: EventEmitter<ISuperTableAddCalculationResult> =
        new EventEmitter<ISuperTableAddCalculationResult>();

    constructor(private localizedTexts: I18NextService) {
        this.prepareBelongingYearSelections();
        this.prepareColumnWidthSelections();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['columnSelectionOptions']?.currentValue) {
            this.prepareNameIdColSelections();
        }

        if (changes['existingCalculation']?.currentValue) {
            this.titleControl.setValue(this.existingCalculation.title);

            const selectedColumns = this.existingCalculation.columnsIds;

            const matchingColumnSelections = this.nameIdColSelections.filter((option) =>
                selectedColumns.includes(option.id),
            );
            this.existingSelectedColumns = cloneDeep<INameId[]>(matchingColumnSelections);

            this.selectedItems = cloneDeep<ISuperTableItemSelection[]>(matchingColumnSelections);

            const existingCalculationColumn = this.columnSelectionOptions.find(
                (option) => option.columnId === this.existingCalculation.calcId,
            );

            this.belongingYearControl.setValue(existingCalculationColumn.belongingYear);

            this.columnWidthControl.setValue(existingCalculationColumn.columnWidth);

            this.calculationOutput.emit({
                title: this.titleControl.value,
                function: ISuperTableCalculationFunction.SUMMATION,
                columnSelections: this.selectedItems,
                rowSelections: this.rowSelectionOptions,
                belongingYear: this.belongingYearControl.value,
                columnWidth: this.columnWidthControl.value,
            });
        }
    }

    ngOnInit(): void {
        this.titleControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            if (this.selectedItems.length !== EMPTY_ARRAY_LENGTH) {
                this.calculationOutput.emit({
                    title: value,
                    function: ISuperTableCalculationFunction.SUMMATION,
                    columnSelections: this.selectedItems,
                    rowSelections: this.rowSelectionOptions,
                    belongingYear: this.belongingYearControl.value,
                    columnWidth: this.columnWidthControl.value,
                });
            }
        });

        this.belongingYearControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            if (this.selectedItems.length !== EMPTY_ARRAY_LENGTH) {
                this.calculationOutput.emit({
                    title: this.titleControl.value,
                    function: ISuperTableCalculationFunction.SUMMATION,
                    columnSelections: this.selectedItems,
                    rowSelections: this.rowSelectionOptions,
                    belongingYear: value,
                    columnWidth: this.columnWidthControl.value,
                });
            }
        });

        this.columnWidthControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            if (this.selectedItems.length !== EMPTY_ARRAY_LENGTH) {
                this.calculationOutput.emit({
                    title: this.titleControl.value,
                    function: ISuperTableCalculationFunction.SUMMATION,
                    columnSelections: this.selectedItems,
                    rowSelections: this.rowSelectionOptions,
                    belongingYear: this.belongingYearControl.value,
                    columnWidth: value,
                });
            }
        });
    }

    onSelectItem(item: INameId): void {
        const index = this.selectedItems.findIndex((selected) => selected.id === item.id);
        if (index === -1) {
            this.selectedItems.push(item);
        } else {
            this.selectedItems.splice(index, 1);
        }

        this.calculationOutput.emit({
            title: this.titleControl.value,
            function: ISuperTableCalculationFunction.SUMMATION,
            columnSelections: this.selectedItems,
            rowSelections: this.rowSelectionOptions,
            belongingYear: this.belongingYearControl.value,
            columnWidth: this.columnWidthControl.value,
        });
    }

    private prepareNameIdColSelections(): void {
        for (const col of this.columnSelectionOptions) {
            if (col.type === SuperTableColumnType.TEXT) {
                continue;
            }
            if (col.type === SuperTableColumnType.CURRENT_YEAR) {
                this.nameIdColSelections.push({
                    id: col.columnId,
                    name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_current_year') as string,
                });
                continue;
            }
            if (col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                this.nameIdColSelections.push({
                    id: col.columnId,
                    name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_previous_year') as string,
                });
                continue;
            }

            this.nameIdColSelections.push({
                id: col.columnId,
                name: col.title[this.language],
            });
        }
    }

    private prepareBelongingYearSelections(): void {
        this.belongingYearSelections.push(
            {
                id: SuperTableColumnBelongingYear.CURRENT_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_current') as string,
            },
            {
                id: SuperTableColumnBelongingYear.PREVIOUS_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_previous') as string,
            },
            {
                id: SuperTableColumnBelongingYear.NO_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_none') as string,
            },
        );
    }

    private prepareColumnWidthSelections(): void {
        this.columnWidthSelections.push(
            {
                id: SuperTableColumnWidth.DYNAMIC,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_dynamic') as string,
            },
            {
                id: SuperTableColumnWidth.SMALL,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_small') as string,
            },
            {
                id: SuperTableColumnWidth.STANDARD,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_standard') as string,
            },
            {
                id: SuperTableColumnWidth.LARGE,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_large') as string,
            },
        );
    }
}
